import { Box, Button, ButtonGroup, Card, Heading, Paragraph, Text } from '@zillow/constellation-10';

import type { ReactNode } from 'react';

type EmptyStateAction = {
  label: string;
  href?: string;
  onClick?: () => void;
};

type EmptyStateProps = {
  icon: ReactNode;
  heading: string;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  body?: ReactNode;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  footer?: string;
  align?: 'center' | 'left';
  'data-testid'?: string;
};

const renderButton = (action: EmptyStateAction, emphasis: 'filled' | 'outlined') => {
  if (action.href) {
    return (
      <Button tone="brand" emphasis={emphasis} asChild>
        <a href={action.href}>{action.label}</a>
      </Button>
    );
  }
  return (
    <Button tone="brand" emphasis={emphasis} onClick={action.onClick}>
      {action.label}
    </Button>
  );
};

const EmptyState = ({
  icon,
  heading,
  headingLevel = 2,
  body,
  primaryAction,
  secondaryAction,
  footer,
  align = 'left',
  'data-testid': dataTestId,
}: EmptyStateProps) => {
  const isCentered = align === 'center';
  const hasActions = !!(primaryAction || secondaryAction);
  const hasBothActions = !!(primaryAction && secondaryAction);

  return (
    <Card
      tone="neutral"
      elevated={false}
      data-testid={dataTestId}
      css={{ paddingInline: 'looser', paddingBlock: 'loosest', textAlign: isCentered ? 'center' : 'left' }}
    >
      <Box aria-hidden="true" css={{ marginBottom: 'default' }}>
        {icon}
      </Box>

      <Heading level={headingLevel} textStyle="heading-xs" css={{ marginBottom: body ? 'tightest' : 'default' }}>
        {heading}
      </Heading>

      {body && <Paragraph css={{ marginBottom: hasActions ? 'default' : undefined }}>{body}</Paragraph>}

      {hasBothActions && (
        <ButtonGroup
          aria-label="Empty state actions"
          orientation="horizontal"
          css={{
            flexDirection: { base: 'column', md: isCentered ? 'row-reverse' : 'row' },
            alignItems: { base: 'stretch', md: 'start' },
            ...(isCentered && { justifyContent: { md: 'center' } }),
          }}
        >
          {renderButton(primaryAction, 'filled')}
          {renderButton(secondaryAction, 'outlined')}
        </ButtonGroup>
      )}
      {!hasBothActions && primaryAction && renderButton(primaryAction, 'filled')}
      {!hasBothActions && secondaryAction && renderButton(secondaryAction, 'outlined')}

      {footer && (
        <Text css={{ marginTop: 'default', display: 'block' }} textStyle="fineprint">
          {footer}
        </Text>
      )}
    </Card>
  );
};

export default EmptyState;
